import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import './Home.css';
import './Dashboard.css';
import Modal from 'react-modal';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import Seo from '../components/Seo';
import NomineeCard from '../components/NomineeCard';
import AddNomineeModal from '../components/AddNomineeModal';
import {
  ASSET_TYPES,
  PROVIDER_OTHER,
  getProvidersForAssetType,
  resolveProviderPreset,
  validateAssetType,
  validateContactRequirement,
  validateFile,
  validateNomineeName,
  validateOptionalEmail,
  validateOptionalPhone,
  validatePolicyNumber,
  validateProviderName,
} from '../config/nomineeForm';
import { PublicHome } from './PublicSite';
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { LuPencil, LuX, LuCloudUpload } from 'react-icons/lu';
import {
  FaPlus,
  FaShieldAlt,
  FaLock,
  FaBell,
  FaUsers,
  FaFileInvoiceDollar,
  FaExclamationTriangle,
  FaUserShield,
  FaSpinner,
  FaCheckDouble
} from 'react-icons/fa';

Modal.setAppElement('#root');

const Home = ({
  user,
  onNavigateApplicationOverview,
  dashboardRefreshKey,
  awaitDashboardLanding = false,
  onDashboardReady,
}) => {
  const dashboardReadySentRef = useRef(false);
  const [nominees, setNominees] = useState([]);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [secureEnabled, setSecureEnabled] = useState(false);
  const [editFormData, setEditFormData] = useState({
    nomineeId: null,
    policyId: null,
    nomineeName: '',
    nomineeEmail: '',
    nomineePhone: '',
    policyDocument: null,
    providerPreset: '',
    providerName: '',
    assetType: '',
    policyNumber: '',
  });
  const [editErrors, setEditErrors] = useState({
    nomineeName: '',
    nomineePhone: '',
    policyDocument: '',
    nomineeEmail: '',
    providerName: '',
    assetType: '',
    policyNumber: '',
    contact: '',
  });
  const [isAddressModalOpen, setAddressModalOpen] = useState(false);
  const [addressSaving, setAddressSaving] = useState(false);
  const [addressForm, setAddressForm] = useState({
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: ''
  });
  const [addressErrors, setAddressErrors] = useState({
    addressLine1: '',
    city: '',
    state: '',
    postalCode: ''
  });
  const [hasAddress, setHasAddress] = useState(false);
  const [secureEnableRequested, setSecureEnableRequested] = useState(false);


  const notifyDashboardReady = useCallback(() => {
    if (!awaitDashboardLanding || !onDashboardReady || dashboardReadySentRef.current) return;
    dashboardReadySentRef.current = true;
    onDashboardReady();
  }, [awaitDashboardLanding, onDashboardReady]);

  useEffect(() => {
    if (!awaitDashboardLanding) {
      dashboardReadySentRef.current = false;
    }
  }, [awaitDashboardLanding]);

  const fetchNominees = useCallback(async () => {
    
    if (user?.id) {
      const token = localStorage.getItem('token');
      setLoading(true);
      setLoadError(false);
      try {
       const response = await axios.get(
  `${process.env.REACT_APP_HOST_SERVER}/api/nominees/find/by/UserId?userId=${user.id}`,
  {
    timeout: 600000,
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  }
);
        setNominees(response.data.data || []);
      } catch (error) {
        console.error('Error fetching nominee data:', error);
        setNominees([]);
        setLoadError(true);
        if (error.code !== 'ECONNABORTED') {
          showNotification('Failed to load nominees. Please refresh the page.', 'error');
        }
      } finally {
        setLoading(false);
        notifyDashboardReady();
      }
    }
  }, [user, notifyDashboardReady]);

  useEffect(() => {
    fetchNominees();
  }, [fetchNominees]);

  // If redirected after login, open Add Nominee modal once phone is on file
  useEffect(() => {
    const shouldOpen = sessionStorage.getItem('openAddNominee');
    if (!user || shouldOpen !== '1') return;

    const candidates = [user.contact, user.phone, user.phoneNumber, user.mobile, user.mobileNumber]
      .filter(Boolean)
      .map(String);
    const hasPhone = candidates.some((value) => {
      const digits = value.replace(/\D/g, '').slice(-10);
      return /^\d{10}$/.test(digits);
    });

    if (hasPhone) {
      sessionStorage.removeItem('openAddNominee');
      setModalOpen(true);
    }
  }, [user]);

  const handleRemove = async (nomineeId, policyId) => {
    const confirmMessage = `Are you sure you want to remove this nominee?\n\nThis action cannot be undone and the nominee will no longer have access to this policy information.`;
    const token= localStorage.getItem('token');

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_HOST_SERVER}/api/nominees/${nomineeId}/policy/${policyId}`,
        { timeout: 10000, headers: {
      'Authorization': `Bearer ${token}`,
    } }
      );
      if (response.data?.successful) {
        setNominees(nominees.filter(n => n.nomineeId !== nomineeId));
        showNotification('Nominee removed successfully! 🗑️', 'success');
      } else {
        showNotification(
          response.data?.message || 'Failed to remove nominee. Please try again.',
          'error'
        );
      }
    } catch (error) {
      console.error('Error removing nominee:', error);
      showNotification('Failed to remove nominee. Please try again.', 'error');
    }
  };

  const handleEditNominee = (nominee) => {
    const providerPreset = resolveProviderPreset(nominee.providerName, nominee.assetType);
    setEditFormData({
      nomineeId: nominee.nomineeId,
      policyId: nominee.policyId,
      nomineeName: nominee.nomineeName || '',
      nomineeEmail: nominee.nomineeEmail || '',
      nomineePhone: nominee.nomineePhone || '',
      providerPreset,
      providerName: providerPreset === PROVIDER_OTHER ? (nominee.providerName || '') : '',
      assetType: nominee.assetType || '',
      policyNumber: nominee.policyNumber || '',
      policyDocument: null,
    });
    setEditErrors({
      nomineeName: '',
      nomineePhone: '',
      policyDocument: '',
      nomineeEmail: '',
      providerName: '',
      assetType: '',
      policyNumber: '',
      contact: '',
    });
    setEditModalOpen(true);
  };

  const handleDocumentClick = async (event, nominee) => {
    event.preventDefault();
    event.stopPropagation();
    if (!nominee?.hasDocument || !nominee?.policyId) {
      showNotification('Document is not available yet. Please upload a file first.', 'error');
      return;
    }
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_HOST_SERVER}/api/policies/${nominee.policyId}/document-url`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const url = response.data?.data?.url;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        showNotification('Unable to open document. Please try again.', 'error');
      }
    } catch (error) {
      showNotification('Unable to open document. Please try again.', 'error');
    }
  };

  const showNotification = (message, type = 'success') => {
    const notification = document.createElement('div');
    notification.innerHTML = `
      <div style="display: flex; align-items: center; gap: 0.5rem;">
        ${type === 'success' ? '✅' : '❌'}
        <span>${message}</span>
      </div>
    `;

    const bgColor = type === 'success' ? '#10b981' : '#ef4444';
    notification.style.cssText = `
      position: fixed; top: 20px; right: 20px; z-index: 9999;
      background: ${bgColor}; color: white; padding: 1rem 1.5rem;
      border-radius: 12px; box-shadow: 0 10px 25px rgba(0,0,0,0.2);
      font-weight: 500; font-family: 'Inter', sans-serif;
      animation: slideInRight 0.3s ease-out, fadeOut 0.3s ease-in 3.7s;
      max-width: 400px; word-wrap: break-word;
    `;

    // Add animation keyframes if not already added
    if (!document.getElementById('notification-styles')) {
      const style = document.createElement('style');
      style.id = 'notification-styles';
      style.textContent = `
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes fadeOut {
          from { opacity: 1; }
          to { opacity: 0; }
        }
      `;
      document.head.appendChild(style);
    }

    document.body.appendChild(notification);
    setTimeout(() => {
      if (document.body.contains(notification)) {
        document.body.removeChild(notification);
      }
    }, 4000);
  };

  const handleEditInputChange = (field, value) => {
    setEditFormData((prev) => ({ ...prev, [field]: value }));

    if (editErrors[field] || ((field === 'nomineeEmail' || field === 'nomineePhone') && editErrors.contact)) {
      setEditErrors((prev) => ({
        ...prev,
        [field]: '',
        contact: field === 'nomineeEmail' || field === 'nomineePhone' ? '' : prev.contact,
      }));
    }
  };

  const handleEditProviderPresetChange = (preset) => {
    setEditFormData((prev) => ({
      ...prev,
      providerPreset: preset,
      providerName: preset === PROVIDER_OTHER ? prev.providerName : '',
    }));
    if (editErrors.providerName) {
      setEditErrors((prev) => ({ ...prev, providerName: '' }));
    }
  };

  const handleEditAssetTypeChange = (assetType) => {
    const nextProviders = getProvidersForAssetType(assetType);
    setEditFormData((prev) => {
      const keepPreset = nextProviders.includes(prev.providerPreset) ? prev.providerPreset : '';
      return {
        ...prev,
        assetType,
        providerPreset: keepPreset,
        providerName: keepPreset === PROVIDER_OTHER ? prev.providerName : '',
      };
    });
    if (editErrors.assetType || editErrors.providerName) {
      setEditErrors((prev) => ({ ...prev, assetType: '', providerName: '' }));
    }
  };

  const editProviderOptions = getProvidersForAssetType(editFormData.assetType);

  const [secureUpdating, setSecureUpdating] = useState(false);
  const handleSecureToggle = async (event) => {
    const newValue = event.target.checked;
    const prev = secureEnabled;

    if (newValue && !hasAddress) {
      setSecureEnableRequested(true);
      setSecureEnabled(false);
      setAddressModalOpen(true);
      return;
    }

    setSecureEnabled(newValue);
    const token = localStorage.getItem('token');
    try {
      setSecureUpdating(true);
      const resp = await axios.put(
        `${process.env.REACT_APP_HOST_SERVER}/api/user/secure-mode`,
        { isSecure: newValue, userId: user?.id },
        { headers: { 'Authorization': `Bearer ${token}` }, timeout: 15000 }
      );
      const raw =
        (resp.data && (resp.data.isSecure ?? resp.data.secure)) ??
        (resp.data?.data && (resp.data.data.isSecure ?? resp.data.data.secure));
      const confirmed =
        raw === true || raw === false ? raw : newValue;
      setSecureEnabled(Boolean(confirmed));
    } catch (err) {
      console.error("Error updating secure flag:", err);
      setSecureEnabled(prev);
    } finally {
      setSecureUpdating(false);
    }
  };

  // Fetch secure mode state on login and dashboard navigation
  useEffect(() => {
    const fetchSecure = async () => {
      if (!user?.id) return;
      const token = localStorage.getItem('token');
      try {
        const resp = await axios.get(
          `${process.env.REACT_APP_HOST_SERVER}/api/user/secure-mode?userId=${user.id}`,
          { headers: { 'Authorization': `Bearer ${token}` }, timeout: 15000 }
        );
        // Accept boolean from multiple possible shapes and string booleans
        const raw =
          (resp.data && (resp.data.isSecure ?? resp.data.secure)) ??
          (resp.data?.data && (resp.data.data.isSecure ?? resp.data.data.secure));
        const value = raw === true || raw === false ? raw : String(raw).toLowerCase() === 'true';
        setSecureEnabled(Boolean(value));
      } catch (e) {
        console.error('Failed to fetch secure mode:', e);
      }
    };
    fetchSecure();
  }, [user, dashboardRefreshKey]);

  const fetchUserAddress = useCallback(async () => {
    if (!user?.id) {
      setHasAddress(false);
      return;
    }
    const token = localStorage.getItem('token');
    try {
      const resp = await fetch(`${process.env.REACT_APP_HOST_SERVER}/api/address/${user.id}` , {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
      });
      if (!resp.ok) {
        setHasAddress(false);
        return;
      }
      const json = await resp.json();
      const a = json?.data || json;
      const exists = Boolean(
        a &&
        String(a.addressLine1 || '').trim() &&
        String(a.city || '').trim() &&
        String(a.state || '').trim() &&
        (String(a.postalCode || a.pincode || '').trim())
      );
      setHasAddress(exists);
    } catch (e) {
      console.error('Failed to fetch address:', e);
      setHasAddress(false);
    }
  }, [user]);

  useEffect(() => {
    fetchUserAddress();
  }, [fetchUserAddress]);

  const validateAddress = (data) => {
    const errs = { addressLine1: '', city: '', state: '', postalCode: '' };
    if (!String(data.addressLine1 || '').trim()) errs.addressLine1 = 'Address Line 1 is required.';
    if (!String(data.city || '').trim()) errs.city = 'City is required.';
    if (!String(data.state || '').trim()) errs.state = 'State is required.';
    const pin = String(data.postalCode || '').trim();
    if (!pin) {
      errs.postalCode = 'PIN/Postal code is required.';
    } else if (!/^\d{6}$/.test(pin)) {
      errs.postalCode = 'PIN code must be 6 digits.';
    }
    return errs;
  };

  const handleAddressInputChange = (field, value) => {
    setAddressForm(prev => ({ ...prev, [field]: field === 'postalCode' ? value.replace(/\D/g, '').slice(0, 6) : value }));
    if (addressErrors[field]) {
      setAddressErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // (Removed state/district/pincode dropdown logic per request)

  const closeAddressModal = () => {
    setAddressModalOpen(false);
    setAddressForm({ addressLine1: '', addressLine2: '', city: '', state: '', postalCode: '' });
    setAddressErrors({ addressLine1: '', city: '', state: '', postalCode: '' });
    setSecureEnableRequested(false);
  };

  const handleSaveAddress = async () => {
    const errs = validateAddress(addressForm);
    setAddressErrors(errs);
    const hasAnyError = Object.values(errs).some(Boolean);
    if (hasAnyError) {
      showNotification('Please fix the errors in the address form.', 'error');
      return;
    }

    try {
      setAddressSaving(true);
      const token = localStorage.getItem('token');
      const resp = await fetch(`${process.env.REACT_APP_HOST_SERVER}/api/address`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          userId: user?.id,
          addressLine1: addressForm.addressLine1.trim(),
          addressLine2: String(addressForm.addressLine2 || '').trim(),
          city: addressForm.city.trim(),
          state: addressForm.state.trim(),
          postalCode: addressForm.postalCode.trim(),
        }),
      });
      if (!resp.ok) {
        throw new Error('Failed to save address');
      }
      await fetchUserAddress();
      showNotification('Address saved successfully.', 'success');
      // If user intended to enable secure mode, enable it now after successful address save
      if (secureEnableRequested) {
        try {
          setSecureUpdating(true);
          await axios.put(
            `${process.env.REACT_APP_HOST_SERVER}/api/user/secure-mode`,
            { isSecure: true, userId: user?.id },
            { headers: { 'Authorization': `Bearer ${token}` }, timeout: 15000 }
          );
          setSecureEnabled(true);
          setSecureEnableRequested(false);
        } catch (e) {
          console.error('Failed to enable secure mode after address save:', e);
          showNotification('Secure Mode could not be enabled. Please try toggling again.', 'error');
        } finally {
          setSecureUpdating(false);
        }
      }
      closeAddressModal();
    } catch (e) {
      console.error('Failed to save address:', e);
      showNotification('Could not save address. Please try again.', 'error');
    } finally {
      setAddressSaving(false);
    }
  };

  const handleAddNominee = async (formValues) => {
    const token = localStorage.getItem('token');
    setSubmitLoading(true);

    const payload = new FormData();
    payload.append('nomineeName', formValues.nomineeName || formValues.nomineeEmail?.split('@')[0] || 'Nominee');
    if (formValues.nomineeEmail) payload.append('nomineeEmail', formValues.nomineeEmail);
    if (formValues.nomineePhone) payload.append('nomineePhone', formValues.nomineePhone);
    payload.append('providerName', formValues.providerName);
    payload.append('assetType', formValues.assetType);
    if (formValues.policyNumber) payload.append('policyNumber', formValues.policyNumber);
    payload.append('userId', user.id);
    payload.append('policyDocument', formValues.policyDocument);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_HOST_SERVER}/api/nominees/create/via/file`,
        payload,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`,
          },
          timeout: 30000,
        }
      );

      if (response.data?.successful) {
        showNotification('Nominee and asset saved successfully.', 'success');
        closeModal();
        fetchNominees();
      } else {
        showNotification(
          response.data?.message || 'Failed to save nominee and asset. Please try again.',
          'error'
        );
      }
    } catch (error) {
      console.error('Error adding nominee:', error);
      let errorMessage = 'Failed to save nominee and asset. Please try again.';

      if (error.response?.status === 413) {
        errorMessage = 'File is too large. Please upload a file smaller than 2MB.';
      } else if (error.response?.status === 400) {
        errorMessage = 'Invalid data provided. Please check all fields and try again.';
      } else if (error.response?.status === 409) {
        errorMessage = 'This nominee already exists for this policy.';
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = 'Upload timeout. Please check your connection and try again.';
      }

      showNotification(errorMessage, 'error');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleUpdateNominee = async (e) => {
    e.preventDefault();
    const {
      nomineeId,
      policyId,
      nomineeName,
      nomineeEmail,
      nomineePhone,
      policyDocument,
      providerPreset,
      providerName,
      assetType,
      policyNumber,
    } = editFormData;

    const providerValue = providerPreset === PROVIDER_OTHER ? providerName : providerPreset;
    const nextErrors = {
      nomineeName: validateNomineeName(nomineeName),
      nomineeEmail: validateOptionalEmail(nomineeEmail),
      nomineePhone: validateOptionalPhone(nomineePhone),
      contact: validateContactRequirement(nomineeEmail, nomineePhone),
      policyDocument: validateFile(policyDocument, false),
      providerName: validateProviderName(providerValue),
      assetType: validateAssetType(assetType),
      policyNumber: validatePolicyNumber(policyNumber),
    };

    setEditErrors(nextErrors);

    if (Object.values(nextErrors).some(Boolean)) {
      showNotification('Please fix the errors in the form before submitting.', 'error');
      return;
    }

    setEditLoading(true);
    const payload = new FormData();
    payload.append('nomineeName', nomineeName.trim());
    if (nomineeEmail.trim()) payload.append('nomineeEmail', nomineeEmail.trim().toLowerCase());
    if (nomineePhone.trim()) payload.append('nomineePhone', nomineePhone.trim());
    payload.append('providerName', providerValue.trim());
    payload.append('assetType', assetType);
    if (policyNumber.trim()) payload.append('policyNumber', policyNumber.trim());
    payload.append('userId', user.id);
    if (policyDocument) {
      payload.append('file', policyDocument);
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(
        `${process.env.REACT_APP_HOST_SERVER}/api/nominees/${nomineeId}/policy/${policyId}/update`,
        payload,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`,
          },
          timeout: 30000,
        }
      );

      if (response.data.successful) {
        showNotification('Nominee and asset updated successfully.', 'success');
        closeEditModal();
        fetchNominees();
      } else {
        showNotification(
          response.data?.message || 'Failed to update nominee. Please try again.',
          'error'
        );
      }
    } catch (error) {
      console.error('Error updating nominee:', error);
      let errorMessage = 'Failed to update nominee. Please try again.';

      if (error.response?.status === 413) {
        errorMessage = 'File is too large. Please upload a file smaller than 2MB.';
      } else if (error.response?.status === 400) {
        errorMessage = 'Invalid data provided. Please check all fields and try again.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Nominee not found. Please refresh the page and try again.';
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = 'Upload timeout. Please check your connection and try again.';
      }

      showNotification(errorMessage, 'error');
    } finally {
      setEditLoading(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditFormData({
      nomineeId: null,
      policyId: null,
      nomineeName: '',
      nomineeEmail: '',
      nomineePhone: '',
      policyDocument: null,
      providerPreset: '',
      providerName: '',
      assetType: '',
      policyNumber: '',
    });
    setEditErrors({
      nomineeName: '',
      nomineePhone: '',
      policyDocument: '',
      nomineeEmail: '',
      providerName: '',
      assetType: '',
      policyNumber: '',
      contact: '',
    });
  };

  // Dashboard metrics
  if (!user) {
    return <PublicHome />;
  }

  // Dashboard metrics
  const documentCount = nominees.length;
  const nomineeCount = nominees.length;
  const missingNominees = nomineeCount === 0;
  const missingDocuments = documentCount === 0;

  const securityStatLabel = secureEnabled ? 'Protected' : 'Basic';

  // Authenticated user dashboard
  return (
    <>
    <Seo
      title="Family Protection Dashboard"
      description="Manage your assets, nominees, and family protection status in your KeepMyAsset dashboard."
      path="/"
      noindex
    />
      <div className="dash-layout">
        <div className="dash-trust dash-trust--top">
          <FaLock className="dash-trust-icon" aria-hidden="true" />
          <div>
            <strong>Your asset information is protected</strong>
            <span>Never store passwords, PINs or OTPs in KeepMyAsset.</span>
          </div>
        </div>

        <div className="dash-top-bar">
          <button
            type="button"
            className="dash-add-btn dash-add-btn--top"
            onClick={() => setModalOpen(true)}
            disabled={loading}
          >
            <FaPlus aria-hidden="true" />
            Add Nominee
          </button>
        </div>

        {loading ? (
          <div className="dash-skeleton-stats" aria-hidden="true">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="dash-skeleton-stat" />
            ))}
          </div>
        ) : (
          <div className="dash-stats">
            <div className="dash-stat">
              <div className="dash-stat-icon" aria-hidden="true">
                <FaFileInvoiceDollar />
              </div>
              <div className="dash-stat-value">{documentCount}</div>
              <div className="dash-stat-label">Documents</div>
              <div className="dash-stat-hint">{documentCount > 0 ? 'On file' : 'Add your first'}</div>
            </div>
            <div className="dash-stat">
              <div className="dash-stat-icon" aria-hidden="true">
                <FaUsers />
              </div>
              <div className="dash-stat-value">{nomineeCount}</div>
              <div className="dash-stat-label">Nominees</div>
              <div className="dash-stat-hint">{nomineeCount === 1 ? '1 protected' : `${nomineeCount} protected`}</div>
            </div>
            <div className="dash-stat is-success">
              <div className="dash-stat-icon" aria-hidden="true">
                <FaBell />
              </div>
              <div className="dash-stat-value">On</div>
              <div className="dash-stat-label">Notifications</div>
              <div className="dash-stat-hint">Alerts enabled</div>
            </div>
            <div className={`dash-stat ${secureEnabled ? 'is-success' : ''}`}>
              <div className="dash-stat-icon" aria-hidden="true">
                <FaShieldAlt />
              </div>
              <div className="dash-stat-value">{securityStatLabel}</div>
              <div className="dash-stat-label">Security</div>
              <div className="dash-stat-hint">{secureEnabled ? 'Secure mode active' : 'Standard protection'}</div>
            </div>
          </div>
        )}

        {(missingNominees || missingDocuments) && !loading && (
          <div className="dash-alerts">
            {missingDocuments && (
              <div className="dash-alert">
                <FaExclamationTriangle aria-hidden="true" />
                <span>Missing documents — add your first policy or asset record.</span>
              </div>
            )}
            {missingNominees && (
              <div className="dash-alert">
                <FaExclamationTriangle aria-hidden="true" />
                <span>Missing nominees — add someone your family can rely on.</span>
              </div>
            )}
          </div>
        )}

        {nomineeCount > 0 && nomineeCount < 2 && !loading && (
          <div className="dash-alert">
            <FaExclamationTriangle aria-hidden="true" />
            <span>Consider adding more nominees to strengthen your family&apos;s protection.</span>
          </div>
        )}

        <section className="dash-nominees" aria-labelledby="dash-nominees-title">
          <h2 id="dash-nominees-title" className="dash-nominees-title">Protected Nominees</h2>

          <div className={`dash-secure-card ${secureEnabled ? 'is-on' : ''}`}>
            <div className="dash-secure-top">
              <div className="dash-secure-icon" aria-hidden="true">
                <FaLock />
              </div>
              <h3 className="dash-secure-title">Secure Mode</h3>
              <div className="dash-secure-toggle-wrap">
                <span className="dash-secure-toggle-label">{secureEnabled ? 'On' : 'Off'}</span>
                <button
                  type="button"
                  className={`dash-secure-toggle ${secureEnabled ? 'is-on' : ''}`}
                  onClick={() => handleSecureToggle({ target: { checked: !secureEnabled } })}
                  disabled={secureUpdating}
                  aria-pressed={secureEnabled}
                  aria-label={`Secure mode ${secureEnabled ? 'on' : 'off'}`}
                >
                  <span className="dash-secure-toggle-thumb" />
                </button>
              </div>
            </div>
            <p className="dash-secure-desc">
              {secureEnabled
                ? 'Additional verification is required before nominee notification.'
                : 'Require manual verification before notifying your nominees.'}
              <button
                type="button"
                className="dash-secure-info-btn"
                aria-label="Secure mode information"
                data-tooltip-id="secure-mode-tooltip"
              >
                <InfoOutlinedIcon fontSize="inherit" aria-hidden="true" />
              </button>
            </p>
            <ReactTooltip id="secure-mode-tooltip" place="top" content="When Secure Mode is ON, nominee checks require manual verification before any notification is sent." />
          </div>

          {loading ? (
            <div className="dash-nominee-grid" aria-busy="true" aria-label="Loading nominees">
              {[0, 1, 2].map((i) => (
                <div key={i} className="dash-skeleton-card" />
              ))}
            </div>
          ) : loadError ? (
            <div className="dash-error">
              <h3>Unable to load your nominees</h3>
              <p>Please check your connection and try again.</p>
              <button type="button" className="dash-retry-btn" onClick={fetchNominees}>
                Try Again
              </button>
            </div>
          ) : nominees.length > 0 ? (
            <div className="dash-nominee-grid">
              {nominees.map((nominee) => (
                <NomineeCard
                  key={nominee.nomineeId}
                  nominee={nominee}
                  onEdit={handleEditNominee}
                  onRemove={handleRemove}
                  onViewDocument={handleDocumentClick}
                />
              ))}
            </div>
          ) : (
            <div className="dash-empty">
              <div className="dash-empty-icon" aria-hidden="true">
                <FaUserShield />
              </div>
              <h3>Protect the people who matter most</h3>
              <p>
                Add your first nominee and connect them to the assets your family should know about.
                Use the Add Nominee button above to get started.
              </p>
            </div>
          )}

        <AddNomineeModal
          isOpen={isModalOpen}
          onClose={closeModal}
          onSubmit={handleAddNominee}
          submitLoading={submitLoading}
          nominees={nominees}
        />

        {/* Edit Nominee Modal */}
        <Modal
          isOpen={isEditModalOpen}
          onRequestClose={closeEditModal}
          contentLabel="Edit Nominee"
          className="modal"
          overlayClassName="modal-overlay"
        >
          <div className="modal-accent" aria-hidden="true" />
          <button
            type="button"
            className="modal-close-btn"
            onClick={closeEditModal}
            aria-label="Close edit nominee dialog"
            disabled={editLoading}
          >
            <LuX aria-hidden="true" />
          </button>
          <div className="modal-header">
            <div className="modal-icon-badge modal-icon-badge--edit">
              <LuPencil aria-hidden="true" />
            </div>
            <div className="modal-header-text">
              <h2>Edit nominee &amp; asset</h2>
              <p>Update contact details, asset information, or replace the policy document.</p>
            </div>
          </div>
          <form onSubmit={handleUpdateNominee} className="modal-form">
            <div className="form-field">
              <label className="form-label" htmlFor="edit-nominee-name">
                Nominee full name <span className="required-mark">*</span>
              </label>
              <input
                id="edit-nominee-name"
                type="text"
                placeholder="e.g. Priya Sharma"
                className={editErrors.nomineeName ? 'input-error' : ''}
                value={editFormData.nomineeName}
                onChange={(e) => handleEditInputChange('nomineeName', e.target.value)}
                maxLength={80}
                disabled={editLoading}
              />
              {editErrors.nomineeName ? (
                <div className="error-message">
                  <FaExclamationTriangle />
                  {editErrors.nomineeName}
                </div>
              ) : null}
            </div>

            <div className="form-field">
              <label className="form-label" htmlFor="edit-nominee-email">
                Email address
              </label>
              <input
                id="edit-nominee-email"
                type="email"
                placeholder="e.g. john.doe@example.com"
                className={editErrors.nomineeEmail ? 'input-error' : ''}
                value={editFormData.nomineeEmail}
                onChange={(e) => handleEditInputChange('nomineeEmail', e.target.value)}
                maxLength={100}
                disabled={editLoading}
              />
              <p className="field-helper">Provide email, phone, or both. At least one contact method is required.</p>
              {editErrors.nomineeEmail ? (
                <div className="error-message">
                  <FaExclamationTriangle />
                  {editErrors.nomineeEmail}
                </div>
              ) : null}
            </div>

            <div className="form-field">
              <label className="form-label" htmlFor="edit-nominee-phone">
                Phone number
              </label>
              <input
                id="edit-nominee-phone"
                type="tel"
                placeholder="e.g. 9876543210"
                className={editErrors.nomineePhone ? 'input-error' : ''}
                value={editFormData.nomineePhone}
                onChange={(e) => handleEditInputChange('nomineePhone', e.target.value.replace(/\D/g, '').slice(0, 10))}
                maxLength={10}
                disabled={editLoading}
              />
              {editErrors.nomineePhone ? (
                <div className="error-message">
                  <FaExclamationTriangle />
                  {editErrors.nomineePhone}
                </div>
              ) : null}
              {editErrors.contact ? (
                <div className="error-message">
                  <FaExclamationTriangle />
                  {editErrors.contact}
                </div>
              ) : null}
            </div>

            <div className="form-field">
              <label className="form-label" htmlFor="edit-asset-type">
                Asset type <span className="required-mark">*</span>
              </label>
              <select
                id="edit-asset-type"
                className={`modal-select ${editErrors.assetType ? 'input-error' : ''}`}
                value={editFormData.assetType}
                onChange={(e) => handleEditAssetTypeChange(e.target.value)}
                disabled={editLoading}
              >
                <option value="">Select asset type</option>
                {ASSET_TYPES.map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
              {editErrors.assetType ? (
                <div className="error-message">
                  <FaExclamationTriangle />
                  {editErrors.assetType}
                </div>
              ) : null}
            </div>

            <div className="form-field">
              <label className="form-label" htmlFor="edit-provider-preset">
                Provider / institution <span className="required-mark">*</span>
              </label>
              <select
                id="edit-provider-preset"
                className={`modal-select ${editErrors.providerName ? 'input-error' : ''}`}
                value={editFormData.providerPreset}
                onChange={(e) => handleEditProviderPresetChange(e.target.value)}
                disabled={editLoading || !editFormData.assetType}
              >
                <option value="">
                  {editFormData.assetType ? 'Select provider' : 'Select asset type first'}
                </option>
                {editProviderOptions.map((preset) => (
                  <option key={preset} value={preset}>{preset}</option>
                ))}
              </select>
              {!editFormData.assetType ? (
                <p className="field-helper">Choose an asset type to see relevant providers.</p>
              ) : null}
              {editFormData.providerPreset === PROVIDER_OTHER ? (
                <input
                  type="text"
                  placeholder="Enter provider name"
                  className={editErrors.providerName ? 'input-error' : ''}
                  value={editFormData.providerName}
                  onChange={(e) => handleEditInputChange('providerName', e.target.value)}
                  maxLength={50}
                  disabled={editLoading}
                />
              ) : null}
              {editErrors.providerName ? (
                <div className="error-message">
                  <FaExclamationTriangle />
                  {editErrors.providerName}
                </div>
              ) : null}
            </div>

            <div className="form-field">
              <label className="form-label" htmlFor="edit-policy-number">
                Policy / account number
              </label>
              <input
                id="edit-policy-number"
                type="text"
                placeholder="Optional but recommended"
                className={editErrors.policyNumber ? 'input-error' : ''}
                value={editFormData.policyNumber}
                onChange={(e) => handleEditInputChange('policyNumber', e.target.value)}
                maxLength={40}
                disabled={editLoading}
              />
              {editErrors.policyNumber ? (
                <div className="error-message">
                  <FaExclamationTriangle />
                  {editErrors.policyNumber}
                </div>
              ) : null}
            </div>

            <div className="form-field">
              <span className="form-label">
                Policy / asset document
                <span className="label-hint">Optional — leave empty to keep current</span>
              </span>
              <label className="modal-file-upload modal-file-upload--optional">
                <input
                  type="file"
                  accept=".pdf,image/jpeg,image/png,image/jpg"
                  onChange={(e) => handleEditInputChange('policyDocument', e.target.files[0])}
                  disabled={editLoading}
                />
                <LuCloudUpload className="upload-icon" aria-hidden="true" />
                <span className="upload-text">
                  {editFormData.policyDocument ? editFormData.policyDocument.name : 'Choose new document'}
                </span>
                <span className="upload-hint">PDF, JPG or PNG · Max 2MB</span>
              </label>
              {editFormData.policyDocument && (
                <div className="file-preview">
                  <FaFileInvoiceDollar aria-hidden="true" />
                  <span className="file-preview-name">{editFormData.policyDocument.name}</span>
                  <span className="file-preview-size">
                    ({(editFormData.policyDocument.size / 1024 / 1024).toFixed(2)} MB)
                  </span>
                </div>
              )}
              {editErrors.policyDocument && (
                <div className="error-message">
                  <FaExclamationTriangle />
                  {editErrors.policyDocument}
                </div>
              )}
            </div>

            <div className="modal-buttons">
              <button 
                type="submit" 
                className="add-btn"
                disabled={editLoading}
              >
                {editLoading ? (
                  <>
                    <FaSpinner className="spinner" />
                    Updating Nominee...
                  </>
                ) : (
                  <>
                    <FaCheckDouble />
                    Update nominee &amp; asset
                  </>
                )}
              </button>
              <button 
                type="button" 
                className="cancel-btn" 
                onClick={closeEditModal}
                disabled={editLoading}
              >
                Cancel
              </button>
            </div>
          </form>

        </Modal>

        {/* Add Address Modal (on enabling Secure Mode) */}
        <Modal
          isOpen={isAddressModalOpen}
          onRequestClose={closeAddressModal}
          contentLabel="Add Address"
          className="modal"
          overlayClassName="modal-overlay"
        >
          <div className="modal-accent" aria-hidden="true" />
          <button
            type="button"
            className="modal-close-btn"
            onClick={closeAddressModal}
            aria-label="Close add address dialog"
            disabled={addressSaving}
          >
            <LuX aria-hidden="true" />
          </button>
          <div className="modal-header">
            <div className="modal-icon-badge modal-icon-badge--address">
              <FaShieldAlt aria-hidden="true" />
            </div>
            <div className="modal-header-text">
              <h2>Add Address</h2>
              <p>Required to enable secure mode for your account.</p>
            </div>
          </div>
          <div className="modal-form">
            <div className="form-field">
              <span className="form-label">
                Address Line 1 <span className="required-mark">*</span>
              </span>
              <input
                type="text"
                placeholder="House/Flat, Street"
                value={addressForm.addressLine1}
                onChange={(e) => handleAddressInputChange('addressLine1', e.target.value)}
                className={addressErrors.addressLine1 ? 'input-error' : ''}
                disabled={addressSaving}
                maxLength={120}
              />
              {addressErrors.addressLine1 && (
                <div className="error-message">{addressErrors.addressLine1}</div>
              )}
            </div>

            <div className="form-field">
              <span className="form-label">Address Line 2 <span className="label-hint">Optional</span></span>
              <input
                type="text"
                placeholder="Area, Landmark"
                value={addressForm.addressLine2}
                onChange={(e) => handleAddressInputChange('addressLine2', e.target.value)}
                disabled={addressSaving}
                maxLength={120}
              />
            </div>

            <div className="form-field">
              <span className="form-label">
                City <span className="required-mark">*</span>
              </span>
              <input
                type="text"
                placeholder="City"
                value={addressForm.city}
                onChange={(e) => handleAddressInputChange('city', e.target.value)}
                className={addressErrors.city ? 'input-error' : ''}
                disabled={addressSaving}
                maxLength={60}
              />
              {addressErrors.city && (
                <div className="error-message">{addressErrors.city}</div>
              )}
            </div>

            <div className="form-field">
              <span className="form-label">
                State <span className="required-mark">*</span>
              </span>
              <input
                type="text"
                placeholder="State"
                value={addressForm.state}
                onChange={(e) => handleAddressInputChange('state', e.target.value)}
                className={addressErrors.state ? 'input-error' : ''}
                disabled={addressSaving}
                maxLength={60}
              />
              {addressErrors.state && (
                <div className="error-message">{addressErrors.state}</div>
              )}
            </div>

            <div className="form-field">
              <span className="form-label">
                PIN Code <span className="required-mark">*</span>
              </span>
              <input
                type="tel"
                placeholder="6-digit PIN"
                value={addressForm.postalCode}
                onChange={(e) => handleAddressInputChange('postalCode', e.target.value)}
                className={addressErrors.postalCode ? 'input-error' : ''}
                disabled={addressSaving}
                maxLength={6}
              />
              {addressErrors.postalCode && (
                <div className="error-message">{addressErrors.postalCode}</div>
              )}
            </div>

            <div className="modal-buttons">
              <button
                type="button"
                className="add-btn"
                onClick={handleSaveAddress}
                disabled={addressSaving}
              >
                {addressSaving ? 'Saving...' : 'Save Address'}
              </button>
              <button
                type="button"
                className="cancel-btn"
                onClick={closeAddressModal}
                disabled={addressSaving}
              >
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      </section>
    </div>
    </>
    
  );
};

export default Home;
